import { cloneDeep } from 'lodash';
import { lbApiBeta } from '@/utils/axiosConfig';
import {
  getPinsEndpoint,
  pinsToMarkers,
  transformSwapResponse
} from '../mapHelpers';
import { FILTER_SETUP, SWAP_FILTER_TYPES } from '../swapList';

function handleMaxValues(filter) {
  if (filter?.rent?.max >= FILTER_SETUP.rent.max) {
    filter.rent.max = 100000000;
  }
  if (filter?.rooms?.max >= FILTER_SETUP.rooms.max) {
    filter.rooms.max = 100000000;
  }
  if (filter?.floor?.max >= FILTER_SETUP.floor.max) {
    filter.floor.max = 100000000;
  }
  if (filter?.sqm?.max >= FILTER_SETUP.sqm.max) {
    filter.sqm.max = 100000000;
  }
}

// Monotonic id for pins requests: rapid tab switches fire overlapping
// fetches, and without this the LAST response to arrive would win SET_PINS
// even when it belongs to a tab the user already left.
let pinsRequestSeq = 0;

// Monotonic id for the current selection: a lazy page fetch resolving after
// the user clicked another pin must not splice its card into the new
// selection's placeholder list.
let selectionSeq = 0;

export default {
  async fetchPins(
    { commit, state, rootGetters },
    { propositionId, endpoint, filter }
  ) {
    const requestId = ++pinsRequestSeq;
    // The cached swap details are pair-specific — drop them when the own
    // proposition changes so a persistent map never serves stale cards.
    if (state && state.pinsPropositionId !== propositionId) {
      commit('CLEAR_SUGGESTION_CACHE');
    }
    commit('SET_PINS_PROPOSITION_ID', propositionId);
    commit('SET_LOADING_PINS', true);
    // No endpoint = the unanswered tab's — keep getPinsEndpoint the single
    // definition so the includeOutsideWishes flag cannot diverge.
    const resolved = endpoint || getPinsEndpoint(SWAP_FILTER_TYPES.UNANSWERED);
    try {
      let response;
      if (resolved.method === 'POST') {
        const body = { propositionId };
        if (filter) {
          const f = cloneDeep(filter);
          handleMaxValues(f);
          body.filter = f;
          body.pageSize = 10000;
        }
        response = await lbApiBeta.post(resolved.url, body);
      } else {
        response = await lbApiBeta.get(
          resolved.url.replace('{propositionId}', propositionId)
        );
      }
      if (requestId !== pinsRequestSeq) return;
      const markers = pinsToMarkers(response.data);
      // Blur-address experiment: an own freemium proposition sees all
      // suggestions as approximate-area circles instead of exact pins.
      // MapView derives the circle geometry (~200 m) itself.
      if (rootGetters?.['myPropositions/hideExactAddress']) {
        markers.forEach(marker => {
          marker.markerType = 'circle';
        });
      }
      commit('SET_PINS', markers);
    } catch (error) {
      if (requestId !== pinsRequestSeq) return;
      console.error('Failed to fetch map pins:', error);
      commit('SET_PINS', []);
    } finally {
      if (requestId === pinsRequestSeq) {
        commit('SET_LOADING_PINS', false);
      }
    }
  },

  async selectPin(
    { commit, state, dispatch },
    { pinId, propositionIds, lazy }
  ) {
    selectionSeq++;
    commit('SET_SELECTED_PIN_ID', pinId);

    const ids = propositionIds || [pinId];

    if (ids.length === 1) {
      commit('SET_SELECTED_SUGGESTIONS', []);
      if (state.suggestionCache[pinId]) {
        commit('SET_SELECTED_SUGGESTION', state.suggestionCache[pinId]);
        return;
      }
      dispatch('fetchSwapDetails', { propositionId: pinId });
    } else if (lazy) {
      // Paginated view: placeholders give the footer its total up front,
      // but each swap is fetched first when its page is shown.
      commit('SET_SELECTED_SUGGESTION', null);
      commit(
        'SET_SELECTED_SUGGESTIONS',
        ids.map(id => ({ propositionId: id, pending: true }))
      );
      dispatch('fetchSuggestionAtIndex', { index: 0 });
    } else {
      commit('SET_SELECTED_SUGGESTION', null);
      dispatch('fetchMultipleSwapDetails', { propositionIds: ids });
    }
  },

  // Lazy page load for the paginated marker card: resolve the placeholder
  // at `index` from cache or the API; no-op when it is already loaded.
  async fetchSuggestionAtIndex({ commit, state, rootState }, { index }) {
    const requestSelection = selectionSeq;
    const current = state.selectedSuggestions[index];
    if (!current || !current.pending) return;

    const id = current.propositionId;
    if (state.suggestionCache[id]) {
      commit('SET_SUGGESTION_AT_INDEX', {
        index,
        suggestion: state.suggestionCache[id]
      });
      return;
    }

    commit('SET_LOADING_SUGGESTION', true);
    try {
      const userPropositionId =
        state.pinsPropositionId ||
        rootState.myPropositions.primaryProposition.propositionId;

      const response = await lbApiBeta.get(
        `/api/swaps/find/${userPropositionId}/${id}`
      );
      const swap = transformSwapResponse(response.data);
      commit('CACHE_SUGGESTION', { id, suggestion: swap });
      // The user may have selected another pin while the request was in
      // flight — its indices belong to a different placeholder list.
      if (requestSelection === selectionSeq) {
        commit('SET_SUGGESTION_AT_INDEX', { index, suggestion: swap });
      }
    } catch (error) {
      console.error('Failed to fetch swap details:', error);
    } finally {
      commit('SET_LOADING_SUGGESTION', false);
    }
  },

  deselectPin({ commit }) {
    selectionSeq++;
    commit('SET_SELECTED_PIN_ID', null);
    commit('SET_SELECTED_SUGGESTION', null);
    commit('SET_SELECTED_SUGGESTIONS', []);
  },

  async fetchSwapDetails({ commit, state, rootState }, { propositionId }) {
    commit('SET_LOADING_SUGGESTION', true);
    try {
      // Same own proposition as the pins (route-driven), not blindly primary.
      const userPropositionId =
        state.pinsPropositionId ||
        rootState.myPropositions.primaryProposition.propositionId;

      const response = await lbApiBeta.get(
        `/api/swaps/find/${userPropositionId}/${propositionId}`
      );

      const swap = transformSwapResponse(response.data);
      commit('SET_SELECTED_SUGGESTION', swap);
      commit('CACHE_SUGGESTION', {
        id: propositionId,
        suggestion: swap
      });
    } catch (error) {
      console.error('Failed to fetch swap details:', error);
      commit('SET_SELECTED_SUGGESTION', null);
    } finally {
      commit('SET_LOADING_SUGGESTION', false);
    }
  },

  async fetchMultipleSwapDetails(
    { commit, state, rootState },
    { propositionIds }
  ) {
    commit('SET_LOADING_SUGGESTION', true);
    try {
      const userPropositionId =
        state.pinsPropositionId ||
        rootState.myPropositions.primaryProposition.propositionId;

      const results = await Promise.all(
        propositionIds.map(async id => {
          if (state.suggestionCache[id]) {
            return state.suggestionCache[id];
          }
          try {
            const response = await lbApiBeta.get(
              `/api/swaps/find/${userPropositionId}/${id}`
            );
            const swap = transformSwapResponse(response.data);
            commit('CACHE_SUGGESTION', { id, suggestion: swap });
            return swap;
          } catch {
            return null;
          }
        })
      );

      commit('SET_SELECTED_SUGGESTIONS', results.filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch multiple swap details:', error);
      commit('SET_SELECTED_SUGGESTIONS', []);
    } finally {
      commit('SET_LOADING_SUGGESTION', false);
    }
  }
};
